import { useTranslation } from 'react-i18next';
import type { ComuneFull } from '@/data';

// Synthetic monthly breakdowns derived from the annual aggregates we already store.
// Until the pipeline produces real monthly arrays, this gives the *shape* of the year
// (peak in July, trough in January, etc.) using physical/climatological models. The
// values are clearly labelled as estimates in the UI so users don't mistake them for
// measurements.

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;

function temperatureMonths(t_jul: number, t_jan: number): number[] {
  // Centered cosine, amplitude = (t_jul - t_jan)/2, mean = (t_jul + t_jan)/2
  // We anchor on observed t_jul and t_jan (which we DO have) so endpoints are exact.
  const amplitude = (t_jul - t_jan) / 2;
  const mid = (t_jul + t_jan) / 2;
  return Array.from({ length: 12 }, (_, i) => {
    const phase = ((i - 6) / 6) * Math.PI; // 0 at Jul, ±π at Jan
    return mid + amplitude * Math.cos(phase);
  });
}

// Italian climatology: precipitation tends to peak in spring/autumn (Apr-May, Oct-Nov)
// in the north, with a summer dip on the Adriatic/Alpine. We use a broad bimodal shape.
const PRECIP_WEIGHTS = [0.085, 0.07, 0.08, 0.09, 0.10, 0.07, 0.05, 0.06, 0.085, 0.115, 0.105, 0.095];

// Humidity: higher in cool months due to lower temperatures; mild oscillation.
const HUMIDITY_BIAS = [1.08, 1.05, 1.00, 0.97, 0.95, 0.93, 0.92, 0.95, 1.00, 1.05, 1.08, 1.09];

// Sunshine days: peak in summer, minimum in Dec-Jan.
const SUNNY_WEIGHTS = [0.045, 0.055, 0.075, 0.085, 0.105, 0.115, 0.135, 0.125, 0.105, 0.075, 0.045, 0.035];

// Returns null when there is no annual total to spread out. A 12-month row of
// zeros would be a claim ("no rain all year"), not an absence of data — so the
// caller drops the chart instead of drawing a flat zero line.
function distributeAnnual(total: number | null | undefined, weights: number[]): number[] | null {
  if (total == null || !isFinite(total)) return null;
  return weights.map((w) => total * w);
}

function clampHumidity(arr: number[]): number[] {
  return arr.map((v) => Math.max(0, Math.min(100, v)));
}

type Color = { stops: string[] };

const TEMP_COLOR: Color = {
  // diverging cold→hot
  stops: ['#3b82f6', '#60a5fa', '#bae6fd', '#fde68a', '#fb923c', '#dc2626'],
};
const RAIN_COLOR: Color = { stops: ['#dbeafe', '#60a5fa', '#1d4ed8'] };
const HUM_COLOR: Color = { stops: ['#a7f3d0', '#10b981', '#065f46'] };
const SUN_COLOR: Color = { stops: ['#fef3c7', '#fbbf24', '#d97706'] };

function pickColor(value: number, min: number, max: number, color: Color): string {
  if (max <= min) return color.stops[Math.floor(color.stops.length / 2)];
  const t = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const idx = Math.min(color.stops.length - 1, Math.floor(t * color.stops.length));
  return color.stops[idx];
}

type ChartProps = {
  title: string;
  data: number[];
  unit: string;
  decimals: number;
  color: Color;
  invertColorBy?: boolean; // when true, smaller value = "warmer" color (e.g. humidity)
};

function Chart({ title, data, unit, decimals, color }: ChartProps) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  return (
    <div className="py-2">
      <div className="mb-1 flex items-baseline justify-between">
        <h4 className="text-xs font-semibold text-slate-700">{title}</h4>
        <span className="text-[10px] text-slate-400">
          {min.toFixed(decimals)} – {max.toFixed(decimals)} {unit}
        </span>
      </div>
      <div className="flex gap-[2px]">
        {data.map((value, i) => {
          const bg = pickColor(value, min, max, color);
          return (
            <div
              key={i}
              className="flex-1 text-center leading-none"
              style={{ color: bg, fontSize: '7px', fontWeight: 600 }}
            >
              {value.toFixed(decimals)}
            </div>
          );
        })}
      </div>
      <div className="flex h-14 items-stretch gap-[2px]">
        {data.map((value, i) => {
          const heightPct = ((value - min) / range) * 100;
          const bg = pickColor(value, min, max, color);
          return (
            <div
              key={i}
              className="flex h-full flex-1 flex-col justify-end"
              data-month={MONTH_KEYS[i]}
              data-value={value.toFixed(decimals)}
            >
              <div
                className="w-full rounded-sm transition-opacity hover:opacity-80"
                style={{
                  height: `${Math.max(10, heightPct)}%`,
                  background: bg,
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 grid grid-cols-12 gap-[2px] text-center text-[9px] uppercase text-slate-400">
        {MONTH_KEYS.map((m) => (
          <span key={m}>{m[0]}</span>
        ))}
      </div>
    </div>
  );
}

export function MonthlyCharts({ comune }: { comune: ComuneFull }) {
  const { t } = useTranslation();
  const c = comune.climate;

  // Prefer real monthly arrays from the pipeline; fall back to seasonal estimate if absent.
  const realTemp = c.t_mean_m;
  const realPrecip = c.precip_m;
  const realHum = c.hum_m;
  const realSunny = c.sunny_m;
  const realSnow = c.snow_m;

  // Each series is either measured, or derived from an annual figure we have.
  // When the annual figure is missing too, the series stays null and its chart
  // is not rendered — an estimate needs something real to be estimated from.
  const tempMonths = realTemp ?? temperatureMonths(c.t_jul, c.t_jan);
  const precipMonths = realPrecip ?? distributeAnnual(c.precip, PRECIP_WEIGHTS);
  const annualHum = c.hum;
  const humMonths =
    realHum ?? (annualHum != null ? clampHumidity(HUMIDITY_BIAS.map((b) => annualHum * b)) : null);
  const sunnyMonths = realSunny ?? distributeAnnual(c.sunny, SUNNY_WEIGHTS);

  const allReal = !!(realTemp && realPrecip && realHum && realSunny);

  return (
    <div>
      <p className="mb-1 text-[10px] italic text-slate-400">
        {allReal
          ? t('details.monthly.realData', {
              defaultValue: 'Реальные климатические нормали NASA POWER 1991–2020, с downscale по высоте.',
            })
          : t('details.monthly.disclaimer', {
              defaultValue: 'Сезонная оценка из годовых показателей.',
            })}
      </p>
      <Chart
        title={t('details.monthly.temp', { defaultValue: 'Температура по месяцам' })}
        data={tempMonths}
        unit="°C"
        decimals={1}
        color={TEMP_COLOR}
      />
      {precipMonths && (
        <Chart
          title={t('details.monthly.precip', { defaultValue: 'Осадки по месяцам' })}
          data={precipMonths}
          unit="мм"
          decimals={0}
          color={RAIN_COLOR}
        />
      )}
      {humMonths && (
        <Chart
          title={t('details.monthly.humidity', { defaultValue: 'Влажность по месяцам' })}
          data={humMonths}
          unit="%"
          decimals={0}
          color={HUM_COLOR}
        />
      )}
      {sunnyMonths && (
        <Chart
          title={t('details.monthly.sunny', { defaultValue: 'Солнечных дней по месяцам' })}
          data={sunnyMonths}
          unit=""
          decimals={0}
          color={SUN_COLOR}
        />
      )}
      {realSnow && realSnow.some((v) => v > 0.1) && (
        <Chart
          title={t('details.monthly.snow', { defaultValue: 'Снежных дней по месяцам' })}
          data={realSnow}
          unit=""
          decimals={1}
          color={{ stops: ['#e0e7ff', '#a5b4fc', '#4f46e5'] }}
        />
      )}
    </div>
  );
}
